import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home as HomeIcon,
  Users as UsersIcon,
  Map as MapIcon,
  BarChart3 as BarChartIcon,
  Settings as SettingsIcon,
  FileText as FileTextIcon,
  Calendar as CalendarIcon,
  MessageSquare as MessageSquareIcon,
  X as XIcon
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useWorkshopStore } from '../../stores/workshopStore';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
  description?: string;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user } = useAuthStore();
  const { setFilters: setWorkshopFilters } = useWorkshopStore();

  const navigation: NavigationItem[] = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: HomeIcon,
      description: 'Overview and statistics'
    },
    {
      name: 'Workshops',
      href: '/workshops',
      icon: UsersIcon,
      description: 'Manage workshops and teams'
    },
    {
      name: 'Plant Mapping',
      href: '/plant-mapping',
      icon: MapIcon,
      description: 'Factory layout and processes'
    },
    {
      name: 'Analytics',
      href: '/analytics',
      icon: BarChartIcon,
      description: 'Performance metrics and reports'
    },
    {
      name: 'Planning',
      href: '/planning',
      icon: CalendarIcon,
      description: 'Meeting agenda and Gantt charts'
    },
    {
      name: 'Kata Coaching',
      href: '/kata-coaching',
      icon: MessageSquareIcon,
      description: 'Coaching sessions and 10 questions'
    },
    {
      name: 'Settings',
      href: '/settings',
      icon: SettingsIcon,
      adminOnly: true,
      description: 'System configuration'
    }
  ];

  const isCurrentPath = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  const filteredNavigation = navigation.filter(item => 
    !item.adminOnly || user?.is_admin
  );

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`
        w-64 h-[calc(100vh-4rem)] bg-primary shadow-lg border-r border-primary-600 transform transition-transform duration-300 ease-in-out
        fixed top-16 left-0 z-40
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:shadow-none
      `}>
        {/* Sidebar Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/20 lg:hidden">
          <div className="flex items-center space-x-2">
            <img 
              src="/logos/autoliv_white_logo.png" 
              alt="Autoliv" 
              className="h-6 w-auto"
            />
            <span className="font-semibold text-white">KATA Dashboard</span>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-white hover:text-gray-200 hover:bg-white/10"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <div className="flex flex-col h-full">
          {/* Main Navigation */}
          <nav className="flex-1 mt-4 px-4 overflow-y-auto">
            <div className="space-y-2">
              {filteredNavigation.map((item) => {
                const isActive = isCurrentPath(item.href);
                const Icon = item.icon;
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                     onClick={() => {
                       if (item.href === '/workshops') {
                         setWorkshopFilters({
                           overdue: undefined,
                           status: undefined,
                           statuses: undefined,
                           department: undefined,
                         });
                       }

                       // Only close sidebar on mobile (< 1024px)
                       if (window.innerWidth < 1024) {
                         onClose();
                       }
                    }}
                    className={`
                      group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-colors duration-200
                      ${isActive 
                        ? 'bg-white/20 text-white border-r-2 border-white' 
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                      }
                    `}
                  >
                    <Icon className={`
                      flex-shrink-0 h-5 w-5 mr-3 transition-colors duration-200
                      ${isActive ? 'text-white' : 'text-white/60 group-hover:text-white'}
                    `} />
                    
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{item.name}</div>
                      {item.description && (
                        <div className={`
                          text-xs mt-0.5 truncate
                          ${isActive ? 'text-white/80' : 'text-white/50'}
                        `}>
                          {item.description}
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Bottom Section - User Info and Version */}
          <div className="mt-auto border-t border-white/20 px-4 py-4">
            {/* User Info Section */}
            <div className="px-3 py-2 mb-3">
              <div className="flex items-center space-x-3">
                <div className="h-8 w-8 bg-white rounded-full flex items-center justify-center text-primary text-sm font-medium">
                  {user?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {user?.full_name || user?.username || 'User'}
                  </p>
                  <p className="text-xs text-white/60 truncate">
                    {user?.role_display || 'User'}
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
